/**
 * Binds a TaskDto to the render-prop editors + row callbacks that TaskRow
 * expects. Popover content mounts lazily (Radix only renders it on open), so
 * usePeople() inside PersonEditor fetches on open, not per row.
 *
 * Row click (`onOpen`) opens the preview drawer; the trailing "open full"
 * icon (`onOpenFull`) navigates to the full task page.
 */
import { useNavigate } from '@tanstack/react-router';
import type { TaskRowProps } from '@k-os/ui';
import { StatusEditor, PriorityEditor, DueEditor, PersonEditor } from '../components/task-fields';
import { usePreviewStore } from '../lib/preview-store';
import type { TaskDto } from '../api/tasks';

export function useTaskQuickActions(): (task: TaskDto) => Partial<TaskRowProps> {
  const openPreview = usePreviewStore((s) => s.open);
  const previewingId = usePreviewStore((s) => s.taskId);
  const navigate = useNavigate();
  return (task: TaskDto) => ({
    onOpen: () => openPreview(task.id),
    onOpenFull: () => navigate({ to: '/tasks/item/$id', params: { id: task.id } }),
    previewing: previewingId === task.id,
    renderStatusEditor: (close) => <StatusEditor task={task} onClose={close} />,
    renderPriorityEditor: (close) => <PriorityEditor task={task} onClose={close} />,
    renderDueEditor: (close) => <DueEditor task={task} onClose={close} />,
    renderPersonEditor: (close) => <PersonEditor task={task} onClose={close} />,
  });
}
